import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Snackbar,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch,
  alpha,
  useTheme,
  Fade,
  Stack,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import EditIcon from "@mui/icons-material/Edit";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import HistoryIcon from "@mui/icons-material/History";
import SyncIcon from "@mui/icons-material/Sync";
import logo from "../assets/oceanlogix.png";
import LoginSettingsModal from "./LoginSettingsModal";
import { useUserContext } from "../Contexts/UserContext";
import { useCompanyContext } from "../Contexts/CompanyContext";
import { useVoyageContextV2 } from "../Contexts/VoyageContextV2";
import { useShipSettings } from "../Contexts/ShipSettingsContext";
import { useTranslation } from "react-i18next";
import { prepareAssignedVessel } from "./login/prepareAssignedVessel";
import { localAuthService } from "../services/auth/localAuthService";

const formatDisplayedSyncTime = (value, isoValue) => {
  if (isoValue) {
    const date = new Date(isoValue);
    if (!Number.isNaN(date.getTime())) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      return `${year}-${month}-${day} ${hours}:${minutes}`;
    }
  }

  if (typeof value === "string") {
    return value.replace(/:\d{2}$/, "");
  }

  return value;
};

const getProvisioningWarningMessage = (warnings, t) => {
  if (!Array.isArray(warnings) || warnings.length === 0) {
    return "";
  }

  const missingUsersWarning = warnings.find(
    (warning) => warning.code === "missing-users"
  );
  if (missingUsersWarning) {
    return t("warning_missing_user_refs", {
      count: missingUsersWarning.count,
    });
  }

  const noActiveAccessWarning = warnings.find(
    (warning) => warning.code === "no-active-access"
  );
  if (noActiveAccessWarning) {
    return t("warning_no_active_vessel_users");
  }

  const missingParticularsWarning = warnings.find(
    (warning) => warning.code === "missing-vessel-particulars"
  );
  if (missingParticularsWarning) {
    return t("warning_missing_vessel_particulars");
  }

  return "";
};

const Login = ({ onLogin, setDarkMode, darkMode }) => {
  const theme = useTheme();
  const { t } = useTranslation("login");

  // Context-hooks
  const { shipData, updateShipData } = useShipSettings();
  const { addLog } = useVoyageContextV2();
  const { setUserData, setUsersData } = useUserContext();
  const { companyLogo, companyName } = useCompanyContext();

  // Lokala states
  const [userName, setUserName] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("error");
  const [userDataLoaded, setUserDataLoaded] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [syncingAssignedVessel, setSyncingAssignedVessel] = useState(false);

  // State för valt skepp och synktid
  const [ship, setShip] = useState(null);
  const [syncTime, setSyncTime] = useState(
    localStorage.getItem("lastSyncTime")
  );
  const [syncTimeIso, setSyncTimeIso] = useState(
    localStorage.getItem("lastSyncTimeIso")
  );

  // 1. Ladda initial data från Context (som läst från localStorage vid boot)
  useEffect(() => {
    if (shipData) {
      setShip(shipData);
      setSyncTime(localStorage.getItem("lastSyncTime"));
      setSyncTimeIso(localStorage.getItem("lastSyncTimeIso"));
    }
  }, [shipData]);

  const isSyncStale = useMemo(() => {
    if (!syncTimeIso) return false;
    const syncedAt = new Date(syncTimeIso);
    if (Number.isNaN(syncedAt.getTime())) return false;

    const twoDaysInMs = 2 * 24 * 60 * 60 * 1000;
    return Date.now() - syncedAt.getTime() > twoDaysInMs;
  }, [syncTimeIso]);

  // 2. Trigger för inloggning när användardata är validerad
  useEffect(() => {
    if (userDataLoaded) {
      onLogin();
      setUserDataLoaded(false);
    }
  }, [userDataLoaded, onLogin]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!userName.trim() || !password.trim()) {
      setSnackbarSeverity("error");
      setSnackbarMessage(t("error_no_credentials"));
      setSnackbarOpen(true);
      return;
    }

    if (!ship) {
      setSnackbarSeverity("error");
      setSnackbarMessage(t("error_select_ship"));
      setSnackbarOpen(true);
      return;
    }

    setLoading(true);

    try {
      const currentShipId = ship.id || ship.shipId;
      const currentShipName = ship.name || ship.baseSettings?.shipName;
      const authResult = await localAuthService.authenticateLocally({
        vesselId: currentShipId,
        username: userName,
        password,
      });

      setUserData(authResult.user);

      void addLog({
        user: authResult.user.username,
        activity: "System Login",
        details: `Logged in to ${currentShipName}`,
      });

      setUserDataLoaded(true);
    } catch (error) {
      setSnackbarSeverity("error");
      setSnackbarMessage(error.message || t("error_login_failed"));
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSyncAssignedVessel = async () => {
    if (!ship) return;

    setSyncingAssignedVessel(true);
    try {
      const {
        shipData: preparedShip,
        userList,
        syncedAtDisplay,
        syncedAtIso: preparedIso,
        warnings,
      } = await prepareAssignedVessel(ship.id || ship.shipId);

      setUsersData(userList);
      updateShipData(preparedShip);
      setShip(preparedShip);
      setSyncTime(syncedAtDisplay);
      setSyncTimeIso(preparedIso);

      const warningMessage = getProvisioningWarningMessage(warnings, t);
      if (warningMessage) {
        setSnackbarSeverity("warning");
        setSnackbarMessage(warningMessage);
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error(error);
      setSnackbarSeverity("error");
      setSnackbarMessage(t("error_sync_failed"));
      setSnackbarOpen(true);
    } finally {
      setSyncingAssignedVessel(false);
    }
  };

  return (
    <Grid
      container
      alignItems="center"
      justifyContent="center"
      sx={{
        height: "100vh",
        background: darkMode
          ? `radial-gradient(circle at center, ${theme.palette.grey[900]} 0%, #000 100%)`
          : `radial-gradient(circle at center, ${alpha(
              theme.palette.primary.light,
              0.2
            )} 0%, ${theme.palette.primary.main} 100%)`,
        p: 2,
      }}
    >
      <Fade in timeout={800}>
        <Paper
          elevation={0}
          sx={{
            p: 5,
            width: "100%",
            maxWidth: 420,
            textAlign: "center",
            borderRadius: 4,
            bgcolor: darkMode ? alpha("#1a1a1a", 0.8) : alpha("#fff", 0.9),
            backdropFilter: "blur(12px)",
            border: "1px solid",
            borderColor: darkMode
              ? alpha("#fff", 0.1)
              : alpha(theme.palette.primary.main, 0.1),
            boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
          }}
        >
          <Snackbar
            open={snackbarOpen}
            autoHideDuration={4000}
            onClose={() => setSnackbarOpen(false)}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <Alert
              severity={snackbarSeverity}
              variant="filled"
              onClose={() => setSnackbarOpen(false)}
            >
              {snackbarMessage}
            </Alert>
          </Snackbar>

          {loading ? (
            <Box sx={{ py: 4 }}>
              <CircularProgress size={60} thickness={4} />
              <Typography
                sx={{ mt: 3, fontWeight: 600, color: "text.secondary" }}
              >
                {t("verifying_identity")}...
              </Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ mb: 4 }}>
                <img
                  src={logo}
                  alt="OceanLogix"
                  style={{ height: 40, marginBottom: 24 }}
                />
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: 1.5,
                  }}
                >
                  {companyLogo && (
                    <img
                      src={companyLogo}
                      alt="Co"
                      style={{ height: 32, borderRadius: 4 }}
                    />
                  )}
                  {!companyLogo && (
                    <Typography
                      variant="h5"
                      sx={{ fontWeight: 800, letterSpacing: -0.5 }}
                    >
                      {t("login")}
                    </Typography>
                  )}
                </Box>
                {companyName && (
                  <Typography
                    variant="body2"
                    sx={{
                      mt: 1.5,
                      color: "text.secondary",
                      fontWeight: 600,
                      letterSpacing: 0.2,
                    }}
                  >
                    {companyName}
                  </Typography>
                )}
              </Box>

              <form onSubmit={handleSubmit}>
                <Box sx={{ mb: 3 }}>
                  <Box
                    onClick={() => setOpenModal(true)}
                    sx={{
                      py: 1.5,
                      px: 2,
                      borderRadius: 2.5,
                      border: "1px solid",
                      borderColor: darkMode ? alpha("#fff", 0.2) : "divider",
                      bgcolor: darkMode
                        ? alpha("#fff", 0.02)
                        : alpha(theme.palette.common.black, 0.015),
                      cursor: "pointer",
                      textAlign: "left",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        bgcolor: darkMode
                          ? alpha("#fff", 0.04)
                          : alpha(theme.palette.common.black, 0.025),
                        borderColor: darkMode
                          ? alpha("#fff", 0.28)
                          : alpha(theme.palette.text.primary, 0.18),
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: "1rem",
                        color: "text.primary",
                        mb: 0.35,
                      }}
                    >
                      {ship
                        ? ship.name || ship.baseSettings?.shipName
                        : t("select_ship_first")}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.75,
                        flexWrap: "wrap",
                        color: "text.secondary",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ fontStyle: "italic", color: "text.secondary" }}
                      >
                        {t("assigned_vessel_hint")}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.45 }}>
                        ·
                      </Typography>
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.35,
                          color: "text.secondary",
                          fontWeight: 600,
                          opacity: 0.85,
                        }}
                      >
                        <EditIcon fontSize="inherit" />
                        <Typography
                          variant="caption"
                          sx={{ color: "inherit", fontWeight: 600 }}
                        >
                          {t("change")}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  {ship && syncTime && (
                    <Stack
                      direction="row"
                      spacing={0.5}
                      alignItems="center"
                      justifyContent="flex-end"
                      sx={{ mt: 0.65, px: 0.5, opacity: 0.55 }}
                    >
                      <HistoryIcon sx={{ fontSize: 14 }} color="action" />
                      <Typography
                        variant="caption"
                        sx={{ fontStyle: "italic" }}
                      >
                        {t("last_sync")}: {formatDisplayedSyncTime(syncTime, syncTimeIso)}
                      </Typography>
                    </Stack>
                  )}

                  {ship && isSyncStale && (
                    <Box
                      sx={{
                        mt: 1.25,
                        px: 1.25,
                        py: 1,
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: alpha(theme.palette.warning.main, 0.35),
                        bgcolor: alpha(theme.palette.warning.main, 0.08),
                        textAlign: "left",
                      }}
                    >
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1}
                        alignItems={{ xs: "flex-start", sm: "center" }}
                        justifyContent="space-between"
                      >
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary", fontWeight: 600 }}
                        >
                          {t("sync_warning_old")}
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={
                            syncingAssignedVessel ? (
                              <CircularProgress size={14} color="inherit" />
                            ) : (
                              <SyncIcon fontSize="small" />
                            )
                          }
                          onClick={handleSyncAssignedVessel}
                          disabled={syncingAssignedVessel}
                          sx={{ borderRadius: 999, fontWeight: 700 }}
                        >
                          {t("sync_now")}
                        </Button>
                      </Stack>
                    </Box>
                  )}
                </Box>

                <TextField
                  fullWidth
                  placeholder={t("username")}
                  variant="outlined"
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  sx={{ mb: 2 }}
                  InputProps={{ sx: { borderRadius: 2 } }}
                />

                <TextField
                  fullWidth
                  placeholder={t("password")}
                  type="password"
                  variant="outlined"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  sx={{ mb: 4 }}
                  InputProps={{ sx: { borderRadius: 2 } }}
                />

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={!ship}
                  startIcon={<LockOpenIcon />}
                  sx={{
                    py: 1.8,
                    borderRadius: 2,
                    fontWeight: 700,
                    fontSize: "1rem",
                  }}
                >
                  {t("signInButton")}
                </Button>
              </form>

              <Box
                sx={{
                  mt: 4,
                  pt: 2,
                  borderTop: "1px solid",
                  borderColor: "divider",
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={darkMode}
                      size="small"
                      onChange={() => {
                        setDarkMode(!darkMode);
                        localStorage.setItem("darkMode", !darkMode);
                      }}
                    />
                  }
                  label={
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                      {darkMode
                        ? t("lightmode").toUpperCase()
                        : t("darkmode").toUpperCase()}
                    </Typography>
                  }
                />
              </Box>
            </>
          )}

          <LoginSettingsModal
            open={openModal}
            onClose={() => setOpenModal(false)}
            ship={ship}
            onShipSelected={(selectedShip) => {
              updateShipData(selectedShip);
              setShip(selectedShip);
              setSyncTime(localStorage.getItem("lastSyncTime"));
              return true;
            }}
            t={t}
          />
        </Paper>
      </Fade>
    </Grid>
  );
};

export default Login;
